import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Star, Heart, Truck, RotateCcw, ChevronRight, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import { getProduct } from "../lib/api";
import { useCart } from "../context/CartContext";
import { eur, waLink } from "../config";
import ProductCard from "../components/ProductCard";
import Reveal from "../components/Reveal";
import { WhatsAppIcon } from "../components/WhatsAppButton";

const RECENT_KEY = "esther_recent";

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem, toggleWishlist, wishlist } = useCart();
  const [data, setData] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const [qty, setQty] = useState(1);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    setData(null); setActiveImg(0); setSize(""); setColor(""); setQty(1);
    getProduct(slug).then((d) => {
      setData(d);
      setColor(d.product.colors?.[0] || "");
      try {
        const prev = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]").filter((r) => r.slug !== slug);
        const entry = { slug: d.product.slug, name: d.product.name, price: d.product.price, image: d.product.images[0] };
        const updated = [entry, ...prev].slice(0, 8);
        localStorage.setItem(RECENT_KEY, JSON.stringify(updated));
        setRecent(prev.slice(0, 4));
      } catch (err) { console.error("Recently viewed storage failed:", err); }
    }).catch(() => navigate("/boutique"));
  }, [slug, navigate]);

  if (!data) return <div className="max-w-7xl mx-auto px-6 py-20 text-center text-ardoise">Chargement...</div>;

  const p = data.product;
  const hasSizes = p.sizes && p.sizes.length > 0;
  const liked = wishlist.includes(p.slug);
  const discount = p.old_price ? Math.round((1 - p.price / p.old_price) * 100) : 0;

  const handleAdd = () => {
    if (hasSizes && !size) { toast.error("Veuillez sélectionner une taille"); return; }
    addItem(p, { size: size || null, color: color || null, quantity: qty });
    toast.success(`${p.name} ajouté au panier`);
  };
  const waMsg = waLink(`Bonjour ! Je suis intéressée par : ${p.name} (${eur(p.price)})${size ? ` - Taille ${size}` : ""}. Est-il disponible ?`);

  return (
    <div data-testid="product-detail-page">
      <div className="max-w-7xl mx-auto px-6 pt-6 text-xs text-ardoise/60 flex items-center gap-1.5">
        <Link to="/" className="hover:text-dore">Accueil</Link><ChevronRight size={12} />
        <Link to={`/boutique?category=${p.category}`} className="hover:text-dore capitalize">{p.category}</Link><ChevronRight size={12} />
        <span className="text-encre">{p.name}</span>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 grid md:grid-cols-2 gap-10 lg:gap-16">
        <div>
          <div className="overflow-hidden bg-white aspect-[3/4]">
            <img src={p.images[activeImg]} alt={p.name} className="w-full h-full object-cover" data-testid="product-main-image" />
          </div>
          <div className="flex gap-3 mt-3">
            {p.images.map((img, i) => (
              <button key={i} onClick={() => setActiveImg(i)} className={`w-20 h-24 overflow-hidden border-2 transition-colors ${activeImg === i ? "border-dore" : "border-transparent"}`} data-testid={`thumb-${i}`}>
                <img src={img} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>

        <div className="md:sticky md:top-28 md:self-start">
          <div className="flex items-center gap-2 mb-2">
            <div className="flex gap-0.5">{Array.from({ length: 5 }).map((_, i) => <Star key={i} size={15} className={i < Math.round(p.rating) ? "fill-dore text-dore" : "text-ardoise/30"} />)}</div>
            <span className="text-xs text-ardoise">{p.rating} · {p.reviews.length} avis</span>
          </div>
          <h1 className="font-display text-3xl md:text-4xl text-encre">{p.name}</h1>
          <div className="flex items-center gap-3 mt-3">
            <span className="font-display text-3xl text-dore">{eur(p.price)}</span>
            {p.old_price && <><span className="text-ardoise/40 line-through text-lg">{eur(p.old_price)}</span><span className="bg-red-600 text-white text-xs px-2 py-1">-{discount}%</span></>}
          </div>

          {p.stock < 5 && <p className="text-red-600 text-sm mt-3">⚡ Plus que {p.stock} en stock !</p>}

          {hasSizes && (
            <div className="mt-6">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-encre font-medium">Taille</span>
                <Link to="/guide-des-tailles" className="text-xs text-dore underline">Guide des tailles</Link>
              </div>
              <div className="flex gap-2">
                {p.sizes.map((s) => (
                  <button key={s} onClick={() => setSize(s)} data-testid={`size-${s}`}
                    className={`w-12 h-12 border text-sm transition-colors ${size === s ? "bg-noir text-white border-noir" : "border-dore/30 hover:border-dore"}`}>{s}</button>
                ))}
              </div>
            </div>
          )}

          {p.colors?.length > 0 && (
            <div className="mt-5">
              <span className="text-sm text-encre font-medium">Couleur : <span className="text-ardoise">{color}</span></span>
              <div className="flex gap-2 mt-2">
                {p.colors.map((c) => (
                  <button key={c} onClick={() => setColor(c)} className={`px-4 h-10 border text-sm transition-colors ${color === c ? "bg-noir text-white border-noir" : "border-dore/30 hover:border-dore"}`}>{c}</button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6 flex items-center gap-4">
            <span className="text-sm text-encre font-medium">Quantité</span>
            <div className="flex items-center border border-dore/30">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-2.5 hover:text-dore"><Minus size={14} /></button>
              <span className="px-4 text-sm">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="p-2.5 hover:text-dore"><Plus size={14} /></button>
            </div>
          </div>

          <div className="mt-7 space-y-3">
            <div className="flex gap-3">
              <button onClick={handleAdd} data-testid="add-to-cart-btn" className="flex-1 bg-noir text-white py-4 text-xs uppercase tracking-widest font-semibold hover:bg-ardoise transition-colors">Ajouter au panier</button>
              <button onClick={() => toggleWishlist(p.slug)} data-testid="wishlist-btn" className="w-14 border border-dore/40 flex items-center justify-center hover:bg-rose/30 transition-colors"><Heart size={20} className={liked ? "fill-rose text-rose" : "text-encre"} /></button>
            </div>
            <a href={waMsg} target="_blank" rel="noreferrer" data-testid="whatsapp-order-btn" className="w-full flex items-center justify-center gap-2 bg-whatsapp text-white py-4 text-xs uppercase tracking-widest font-semibold hover:brightness-95 transition-all"><WhatsAppIcon size={16} /> Commander sur WhatsApp</a>
          </div>

          <div className="mt-6 space-y-2 border-t border-dore/20 pt-5">
            <p className="flex items-center gap-2 text-sm text-ardoise"><Truck size={17} className="text-dore" /> Livraison en Guadeloupe sous 2-5 jours{p.shipping_from ? <> · <b className="text-encre" data-testid="shipping-from">dès {eur(p.shipping_from)}</b></> : null}</p>
            <p className="flex items-center gap-2 text-sm text-ardoise"><RotateCcw size={17} className="text-dore" /> Retour possible sous 7 jours</p>
          </div>

          <div className="mt-6 space-y-4 text-sm text-ardoise leading-relaxed">
            <p>{p.description}</p>
            {p.material && <p><b className="text-encre">Matière :</b> {p.material}</p>}
            {p.care && <p><b className="text-encre">Entretien :</b> {p.care}</p>}
          </div>
        </div>
      </div>

      {p.reviews.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-10 border-t border-dore/20">
          <h2 className="font-display text-2xl text-encre mb-6">Avis clientes</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {p.reviews.map((r, i) => (
              <div key={i} className="bg-white border border-dore/20 p-6">
                <div className="flex gap-0.5 mb-2">{Array.from({ length: r.rating }).map((_, k) => <Star key={k} size={14} className="fill-dore text-dore" />)}</div>
                <p className="font-serif-el italic text-ardoise">"{r.text}"</p>
                <p className="text-encre text-sm font-medium mt-3">{r.name} · <span className="text-ardoise/60 font-normal">{r.commune}</span></p>
              </div>
            ))}
          </div>
        </section>
      )}

      {data.similar.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-12 border-t border-dore/20">
          <h2 className="font-display text-2xl md:text-3xl text-encre text-center mb-10">Produits similaires</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {data.similar.map((s) => <ProductCard key={s.slug} product={s} />)}
          </div>
        </section>
      )}

      {recent.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 pb-16">
          <h3 className="font-display text-xl text-encre mb-5">Vu récemment</h3>
          <div className="grid grid-cols-4 gap-3 md:gap-6">
            {recent.map((r) => (
              <Link key={r.slug} to={`/produit/${r.slug}`} className="group">
                <div className="overflow-hidden hover-zoom aspect-[3/4]"><img src={r.image} alt={r.name} className="w-full h-full object-cover" /></div>
                <p className="text-xs text-encre mt-2 truncate">{r.name}</p>
                <p className="text-dore text-sm">{eur(r.price)}</p>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
